/**
 * Start screen for BARRAGE. The live, teased player ball is rendered by Phaser
 * (StartFlowScene) on the canvas BEHIND this overlay — so this menu must let
 * pointer events fall THROUGH to the canvas everywhere except its own interactive
 * controls, otherwise the drag-to-start gesture wouldn't reach the game. The root
 * is pointer-transparent; each interactive panel re-enables pointer events.
 *
 * DECLUTTERED LAYOUT (owner spec): the live ball is the hero. The overlay is a
 * vertical column — header/title pinned at the TOP, then a big CLEAR centre band
 * (`.menu-ball-zone`) that FLEX-GROWS and is fully pointer-transparent so a press
 * anywhere over it reaches the ball on the canvas, then a COMPACT bottom cluster
 * (`.menu-controls`, pointer-events:auto) holding the stats, the POPSTAR button,
 * the skin chip and a compact leaderboard. CRITICAL: nothing interactive may sit
 * in the vertical centre where the ball lives — every control lives either in the
 * top header (passthrough) or the bottom cluster — or drag-to-start would break.
 *
 * START FLOW (owner's drag-to-start spec): there is NO Play button. The ONLY way
 * to start is to DRAG THE BALL (handled by StartFlowScene on the canvas). Inside
 * the centre band we float an elegant animated ".drag-teaser" hint (pulsing rings
 * + a "Drag to start" label). The label doubles as the keyboard/click fallback —
 * it fires `onPlay`, which sends the `start` command StartFlowScene already
 * understands — so the game stays reachable without the drag gesture. On
 * drag-start (`exiting`) the whole overlay animates away to reveal the game.
 *
 * PROFILE: for a logged-in (named) player the shell hands down their name / level
 * / tokens and we pin a glamorous ".menu-corner-card" (a nod to POPSTAR) to the
 * top corner. Anonymous players see a subtle "log in via POPSTAR" hint instead.
 *
 * Presentational only: no per-frame state, no service/game imports beyond the
 * read-only data shapes the shell hands down.
 */
import { useState } from 'react';
import type { CSSProperties } from 'react';
import type { ScoreEntry, ServicesMode } from '../services';
import type { Skin } from '../game/skins';
import { rankForLevel } from '../core/ranks';
import { Leaderboard } from './Leaderboard';
import { SkinPicker } from './SkinPicker';
import { useT } from './i18n';

/** Read-only profile snapshot the shell reads once via `services.getProfile()`. */
export interface MenuProfile {
  name: string;
  level: number;
  tokens: number;
}

interface MenuProps {
  mode: ServicesMode;
  highScore: number;
  leaderboard: ScoreEntry[];
  skins: readonly Skin[];
  skinId: string;
  /** True while the menu plays its exit animation after the player grabs the ball. */
  exiting?: boolean;
  /**
   * The logged-in player's headline stats for the corner card, or null for an
   * anonymous user (in which case we show a subtle "log in via POPSTAR" hint).
   */
  profile?: MenuProfile | null;
  /** Today's Daily Run top list, for the leaderboard's Today toggle. */
  dailyBoard?: ScoreEntry[];
  onSelectSkin: (id: string) => void;
  /**
   * Drag-to-start fallback: fires the `start` command so keyboard / click users
   * can begin a run without the drag gesture. (No visible Play button — the ball
   * is the start trigger; this backs the "Drag to start" label.)
   */
  onPlay: () => void;
  /** Start today's DAILY RUN (the shared-seed challenge — boots straight in). */
  onPlayDaily: () => void;
  /** Open the POPSTAR network section (profile / collect — login-gated). */
  onOpenNetwork: () => void;
}

/** Let drags fall through to the live ball on the canvas; controls opt back in. */
const PASSTHROUGH: CSSProperties = { pointerEvents: 'none' };
const INTERACTIVE: CSSProperties = { pointerEvents: 'auto' };

/** First glyph of the display name, for the corner-card avatar. */
function initial(name: string): string {
  return (name.trim()[0] ?? '?').toUpperCase();
}

export function Menu({
  mode,
  highScore,
  leaderboard,
  skins,
  skinId,
  exiting = false,
  profile = null,
  dailyBoard = [],
  onSelectSkin,
  onPlay,
  onPlayDaily,
  onOpenNetwork,
}: MenuProps) {
  const t = useT();
  // Which board the compact leaderboard shows: all-time vs today's Daily Run.
  const [boardScope, setBoardScope] = useState<'alltime' | 'daily'>('alltime');
  return (
    <div
      className={`screen menu menu-overlay${exiting ? ' is-exiting fade-scale-out' : ' fade-scale-in'}`}
      style={PASSTHROUGH}
      aria-hidden={exiting}
    >
      {/* Logged-in players get a glamorous corner card; anon users get a hint. */}
      {profile ? (
        <button
          className="menu-corner-card"
          style={INTERACTIVE}
          onClick={onOpenNetwork}
          type="button"
          aria-label={`${profile.name} — level ${profile.level}, ${profile.tokens} tokens. Open POPSTAR.`}
        >
          <span className="menu-corner-avatar" aria-hidden>
            {initial(profile.name)}
          </span>
          <span className="menu-corner-body">
            <span className="menu-corner-name">{profile.name}</span>
            <span className="menu-corner-meta">
              <span className="menu-corner-level">
                Lv {profile.level} · {rankForLevel(profile.level).name}
              </span>
              <span className="menu-corner-tokens">
                <span aria-hidden>★</span>
                {profile.tokens.toLocaleString()}
              </span>
            </span>
          </span>
        </button>
      ) : (
        <button
          className="menu-corner-card menu-corner-hint"
          style={INTERACTIVE}
          onClick={onOpenNetwork}
          type="button"
        >
          <span className="menu-corner-avatar" aria-hidden>
            ★
          </span>
          <span className="menu-corner-body">
            <span className="menu-corner-name">{t('menu.login')}</span>
            <span className="menu-corner-meta">{t('menu.viaPopstar')}</span>
          </span>
        </button>
      )}

      {/* TOP: title + tagline. Passthrough so a high drag still reaches the ball. */}
      <header className="menu-head" style={PASSTHROUGH}>
        <h1 className="title wordmark" data-text="Barrage!">Barrage!</h1>
        <p className="subtitle">{t('menu.tagline')}</p>
      </header>

      {/* CENTRE: a big CLEAR pointer-passthrough band that FLEX-GROWS to fill the
          space between header and controls — the live teased ball sits here and a
          press anywhere over it reaches the canvas. NOTHING interactive may live
          in this band except the floating "Drag to start" treatment, whose rings
          are pointer-transparent and whose label is the keyboard/click fallback. */}
      <div className="menu-ball-zone" style={PASSTHROUGH}>
        <div className="drag-teaser" style={PASSTHROUGH}>
          <span className="drag-teaser-ring" aria-hidden />
          <span className="drag-teaser-ring" aria-hidden />
          <span className="drag-teaser-ring" aria-hidden />
          <button
            className="drag-teaser-label"
            style={INTERACTIVE}
            onClick={onPlay}
            type="button"
          >
            {t('menu.dragToStart')}
          </button>
        </div>
      </div>

      {/* BOTTOM: a compact cluster of every interactive control, kept clear of the
          vertical centre. Opts back into pointer events for its contents. */}
      <div className="menu-controls" style={INTERACTIVE}>
        <div className="stat-row">
          <div className="stat glass">
            <span className="stat-value">{highScore.toLocaleString()}</span>
            <span className="stat-label">{t('menu.yourBest')}</span>
          </div>
        </div>

        {/* DAILY RUN: the shared-seed challenge — same storm for everyone today. */}
        <button
          className="btn btn-ghost menu-network popstar-entry menu-daily-entry"
          onClick={onPlayDaily}
          type="button"
        >
          <span className="popstar-mark menu-daily-mark" aria-hidden>
            🗓
          </span>
          <span className="popstar-text">
            <span className="popstar-title">{t('daily.title')}</span>
            <span className="popstar-sub">{t('daily.menuSub')}</span>
          </span>
          <span className="popstar-chevron" aria-hidden>
            ›
          </span>
        </button>

        <button
          className="btn btn-ghost menu-network popstar-entry"
          onClick={onOpenNetwork}
          type="button"
        >
          <span className="popstar-mark" aria-hidden>
            ★
          </span>
          <span className="popstar-text">
            <span className="popstar-title">POPSTAR</span>
            <span className="popstar-sub">{t('menu.popstarSub')}</span>
          </span>
          <span className="popstar-chevron" aria-hidden>
            ›
          </span>
        </button>

        {/* Compact skin chip — the picker collapses to a single current-skin chip
            and only expands its grid on tap (see .skin-picker-wrap in index.css). */}
        <SkinPicker skins={skins} selectedId={skinId} onSelect={onSelectSkin} />

        {/* Compact leaderboard — a short, glanceable top list in the bottom band,
            with an all-time / today's-daily scope toggle (flat, no pills). */}
        <section className="panel glass menu-leaderboard">
          <div className="menu-board-head">
            <h2>{t('menu.leaderboard')}</h2>
            <div className="menu-board-toggle" role="tablist" aria-label="Leaderboard scope">
              <button
                type="button"
                role="tab"
                aria-selected={boardScope === 'alltime'}
                className={boardScope === 'alltime' ? 'active' : ''}
                onClick={() => setBoardScope('alltime')}
              >
                {t('menu.allTime')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={boardScope === 'daily'}
                className={boardScope === 'daily' ? 'active' : ''}
                onClick={() => setBoardScope('daily')}
              >
                {t('daily.today')}
              </button>
            </div>
          </div>
          {boardScope === 'alltime' ? (
            <Leaderboard entries={leaderboard} />
          ) : dailyBoard.length > 0 ? (
            <Leaderboard entries={dailyBoard} />
          ) : (
            <p className="menu-board-empty">{t('daily.boardEmpty')}</p>
          )}
        </section>
      </div>

      <footer className="backend-badge" data-mode={mode} style={PASSTHROUGH}>
        {mode === 'firebase' ? 'Firebase: live' : 'Firebase: OFF — local mode'}
      </footer>
    </div>
  );
}
